import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronRight, Activity, FileText, Weight, ClipboardList, Heart, Utensils, Dumbbell, User, Eye } from "lucide-react";
import { Pencil, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";

interface GroupedResultsViewProps {
  records: any[];
  onRecordsChange: (updated: any[]) => void;
  contentType?: string;
  defaultExpanded?: boolean;
}

const recordTypeLabels: Record<string, string> = {
  lab_result: "Exames laboratoriais",
  result: "Exame Laboratorial",
  results: "Exame Laboratorial",
  patient_info: "Informações do Paciente",
  body_measurement: "Antropometria",
  anthropometry: "Antropometria",
  clinical_note: "Notas clínicas",
  vital_signs: "Sinais vitais",
  vitals: "Sinais vitais",
  medication: "Medicações",
  procedure: "Procedimentos",
  diagnosis: "Diagnósticos",
  physiotherapy_evolution: "Evolução Fisioterapêutica",
  nutritional_assessment: "Avaliação Nutricional",
  meal_plan: "Plano Alimentar",
  training_plan: "Plano de Treino",
  body_composition: "Composição Corporal",
  psychotherapy_session: "Sessão de Psicoterapia",
  chiropractic_adjustment: "Ajuste Quiroprático",
  aesthetic_protocol: "Protocolo Estético",
  other: "Outros registros"
};

const recordTypeIcons: Record<string, any> = {
  lab_result: Activity,
  patient_info: User,
  body_measurement: Weight,
  clinical_note: FileText,
  vital_signs: Heart,
  vitals: Heart,
  medication: ClipboardList,
  procedure: ClipboardList,
  diagnosis: FileText,
  physiotherapy_evolution: ClipboardList,
  nutritional_assessment: Utensils,
  meal_plan: Utensils,
  training_plan: Dumbbell,
  body_composition: Weight,
  psychotherapy_session: FileText,
  chiropractic_adjustment: ClipboardList,
  aesthetic_protocol: ClipboardList,
  other: FileText
};

export function GroupedResultsView({ records, onRecordsChange, contentType, defaultExpanded = false }: GroupedResultsViewProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  const groupedRecords = records.reduce((acc, record) => {
    const type = record.type || "lab_result";
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(record);
    return acc;
  }, {} as Record<string, any[]>);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (type: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const formatDate = (dateStr: string | null | undefined, recordType?: string): string => {
    if (!dateStr) {
      return format(new Date(), "dd/MMM/yyyy", { locale: ptBR });
    }

    try {
      return format(new Date(dateStr), "dd/MMM/yyyy", { locale: ptBR });
    } catch {
      return format(new Date(), "dd/MMM/yyyy", { locale: ptBR });
    }
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;

    const updated = [...records];
    updated[editingIndex] = {
      ...updated[editingIndex],
      date: editForm.date || updated[editingIndex].date,
      test_name: editForm.test_name || updated[editingIndex].test_name,
      value: editForm.value !== undefined ? parseFloat(editForm.value) : updated[editingIndex].value,
      unit: editForm.unit || updated[editingIndex].unit,
      ref_low: editForm.ref_low ? parseFloat(editForm.ref_low) : updated[editingIndex].ref_low,
      ref_high: editForm.ref_high ? parseFloat(editForm.ref_high) : updated[editingIndex].ref_high,
    };

    onRecordsChange(updated);
    setEditingIndex(null);
    toast.success("Resultado atualizado!");
  };

  const handleDelete = (index: number) => {
    const updated = records.filter((_, i) => i !== index);
    onRecordsChange(updated);
    toast.success("Resultado excluído!");
  };

  const getResultContent = (record: any): JSX.Element => {
    // Caso específico para physiotherapy_evolution
    if (record.type === 'physiotherapy_evolution') {
      // Se tem metrics, mostrar a primeira métrica como principal
      if (record.metrics && Array.isArray(record.metrics) && record.metrics.length > 0) {
        const firstMetric = record.metrics[0];
        const metricName = firstMetric.name || 'Métrica não identificada';
        const metricValue = firstMetric.value !== null && firstMetric.value !== undefined
          ? `${firstMetric.value}${firstMetric.unit ? ` ${firstMetric.unit}` : ''}`
          : '-';

        return (
          <div className="space-y-2">
            <div>
              <span className="text-xs text-muted-foreground">Métrica</span>
              <p className="font-medium text-primary">{metricName}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Resultado</span>
              <p className="font-semibold text-secondary">{metricValue}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Data</span>
              <p className="text-sm">{formatDate(record.date || record.event_date, record.type)}</p>
            </div>

            {/* Mostrar métricas adicionais se houver */}
            {record.metrics.length > 1 && (
              <div className="mt-2 pt-2 border-t">
                <span className="text-xs text-muted-foreground">Outras métricas:</span>
                {record.metrics.slice(1).map((m: any, idx: number) => (
                  <p key={idx} className="text-xs text-muted-foreground mt-1">
                    {m.name}: {m.value}{m.unit ? ` ${m.unit}` : ''}
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      }

      // Fallback se não tiver metrics
      return (
        <div className="space-y-2">
          <div>
            <span className="text-xs text-muted-foreground">Exercício</span>
            <p className="font-medium text-primary">{record.exercise || 'Não informado'}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Data</span>
            <p className="text-sm">{formatDate(record.date || record.event_date, record.type)}</p>
          </div>
        </div>
      );
    }

    const metricMap: Record<string, string> = {
      'peso': 'Peso',
      'altura': 'Altura',
      'imc': 'IMC',
      'circunferencia_cintura': 'Circunferência da Cintura',
      'circunferencia_quadril': 'Circunferência do Quadril',
      'pressao_arterial_sistolica': 'Pressão Arterial Sistólica',
      'pressao_arterial_diastolica': 'Pressão Arterial Diastólica',
      'frequencia_cardiaca': 'Frequência Cardíaca',
      'temperatura': 'Temperatura'
    };

    // Prioritize 'measurement' field over 'measurement_type'
    const measurementValue = record.measurement || record.measurement_type;
    const normalizedMeasurement = measurementValue?.toLowerCase();

    const metricName = record.test_name ||
      (normalizedMeasurement ? (metricMap[normalizedMeasurement] ||
        measurementValue.charAt(0).toUpperCase() + measurementValue.slice(1)) : null) ||
      'Métrica não identificada';

    let resultValue = '-';
    if (record.value !== null && record.value !== undefined) {
      resultValue = `${record.value}${record.unit ? ` ${record.unit}` : ''}`;
    } else if (record.type === 'clinical_note' || record.description) {
      const desc = record.description || record.extracted_content || '';
      resultValue = desc.length > 100 ? desc.substring(0, 97) + '...' : desc;
    } else if (record.extracted_content) {
      const content = record.extracted_content;
      resultValue = content.length > 100 ? content.substring(0, 97) + '...' : content;
    }

    return (
      <div className="space-y-2">
        <div>
          <span className="text-xs text-muted-foreground">Métrica</span>
          <p className="font-medium text-primary">{metricName}</p>
        </div>
        <div>
          <span className="text-xs text-muted-foreground">Resultado</span>
          <p className="font-semibold text-secondary">{resultValue}</p>
        </div>
        <div>
          <span className="text-xs text-muted-foreground">Data</span>
          <p className="text-sm">{formatDate(record.date || record.event_date, record.type)}</p>
        </div>
      </div>
    );
  };

  const totalRecords = records.length;

  if (totalRecords === 0) {
    return null;
  }

  // Calcular totalizadores por tipo
  const resultsByType = Object.entries(groupedRecords)
    .sort((a, b) => (b[1] as any[]).length - (a[1] as any[]).length);

  return (
    <>
      <div className="space-y-4">
        {/* Cabeçalho com totalizadores gerais */}
        <div>
          <h3 className="text-xl font-semibold text-primary mb-3">Resultados</h3>
          <div className="text-sm text-muted-foreground space-y-1">
            {resultsByType.map(([type, typeRecords]) => {
              const recordsArray = typeRecords as any[];
              return (
                <div key={type} className="flex items-center gap-2">
                  <span className="font-medium">{recordTypeLabels[type] || type}:</span>
                  <span className="text-primary font-semibold">{recordsArray.length}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Grupos colapsáveis */}
        {resultsByType.map(([type, typeRecords]) => {
          const recordsArray = typeRecords as any[];
          const isExpanded = expandedGroups.has(type);
          const label = recordTypeLabels[type] || type;
          const Icon = recordTypeIcons[type] || FileText;

          return (
            <Collapsible key={type} open={isExpanded} onOpenChange={() => toggleGroup(type)}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        )}
                        <Icon className="h-5 w-5 text-primary" />
                        <CardTitle className="text-base font-medium">{label}</CardTitle>
                      </div>
                      <Badge variant="outline">{recordsArray.length}</Badge>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {recordsArray.map((record, idx) => {
                        const originalIndex = records.indexOf(record);
                        const metricName = record.test_name ||
                          (record.measurement || record.measurement_type) ||
                          (record.metrics && record.metrics[0]?.name) ||
                          'Métrica não identificada';
                        const resultValue = record.value !== null && record.value !== undefined
                          ? `${record.value}${record.unit ? ` ${record.unit}` : ''}`
                          : '-';
                        const displayDate = formatDate(record.date || record.event_date, record.type);

                        return (
                          <Card key={idx} className="overflow-hidden">
                            <Collapsible defaultOpen={false}>
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="p-1.5 bg-primary/10 rounded shrink-0">
                                      <Icon className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-sm text-primary">
                                          {metricName}
                                        </span>
                                        <span className="text-sm font-semibold text-foreground">
                                          {resultValue}
                                        </span>
                                        {displayDate && (
                                          <span className="text-xs text-muted-foreground">
                                            {displayDate}
                                          </span>
                                        )}
                                      </div>
                                      {(record.ref_low || record.ref_high) && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                          Ref: {record.ref_low ?? '—'} - {record.ref_high ?? '—'} {record.unit || ''}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <CollapsibleTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                      >
                                        <Eye className="h-3.5 w-3.5" />
                                      </Button>
                                    </CollapsibleTrigger>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setEditForm(record);
                                        setEditingIndex(originalIndex);
                                      }}
                                      className="h-7 w-7 p-0"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDelete(originalIndex)}
                                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </div>

                                {/* JSON em Dropdown */}
                                <CollapsibleContent className="pt-3">
                                  <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
                                    {JSON.stringify(record, null, 2)}
                                  </pre>
                                </CollapsibleContent>
                              </CardContent>
                            </Collapsible>
                          </Card>
                        );
                      })}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          );
        })}
      </div>

      {/* Dialog de Edição */}
      <AlertDialog open={editingIndex !== null} onOpenChange={(open) => !open && setEditingIndex(null)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-primary">Editar Resultado</AlertDialogTitle>
            <AlertDialogDescription>
              Modifique os campos abaixo e clique em Salvar para atualizar o resultado.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="date">Data</Label>
              <Input
                id="date"
                type="date"
                value={editForm.date || ""}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="testName">Nome do Exame</Label>
              <Input
                id="testName"
                value={editForm.test_name || ""}
                onChange={(e) => setEditForm({ ...editForm, test_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="value">Valor</Label>
                <Input
                  id="value"
                  type="number"
                  step="any"
                  value={editForm.value || ""}
                  onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="unit">Unidade</Label>
                <Input
                  id="unit"
                  value={editForm.unit || ""}
                  onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="refLow">Ref. Mínima</Label>
                <Input
                  id="refLow"
                  type="number"
                  step="any"
                  value={editForm.ref_low || ""}
                  onChange={(e) => setEditForm({ ...editForm, ref_low: e.target.value })}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="refHigh">Ref. Máxima</Label>
                <Input
                  id="refHigh"
                  type="number"
                  step="any"
                  value={editForm.ref_high || ""}
                  onChange={(e) => setEditForm({ ...editForm, ref_high: e.target.value })}
                />
              </div>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveEdit} className="bg-primary">
              Salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
